import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const DistributorContext = createContext(null);
const BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Distributor session — held in localStorage + verified against /me on mount.
 * Provides axios instance with Authorization header pre-set.
 */
export function DistributorProvider({ children }) {
  const [user, setUser] = useState(null);          // null = checking, false = anonymous, object = logged in
  const [token, setToken] = useState(() => localStorage.getItem("dist_token") || null);

  const api = axios.create({ baseURL: `${BASE}/api`, timeout: 15000 });
  if (token) api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  const refresh = useCallback(async (tok = token) => {
    if (!tok) {
      setUser(false);
      return;
    }
    try {
      const r = await axios.get(`${BASE}/api/distributors/me`, {
        headers: { Authorization: `Bearer ${tok}` },
        timeout: 10000,
      });
      setUser(r.data.user);
    } catch {
      // token expired / invalid
      localStorage.removeItem("dist_token");
      setToken(null);
      setUser(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const r = await axios.post(`${BASE}/api/distributors/login`, { email, password });
    const tok = r.data.token;
    localStorage.setItem("dist_token", tok);
    setToken(tok);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (payload) => {
    const r = await axios.post(`${BASE}/api/distributors/register`, payload);
    return r.data;
  };

  const logout = () => {
    localStorage.removeItem("dist_token");
    setToken(null);
    setUser(false);
  };

  return (
    <DistributorContext.Provider value={{ user, token, api, login, register, logout, refresh }}>
      {children}
    </DistributorContext.Provider>
  );
}

export const useDistributor = () => useContext(DistributorContext);
